<script setup lang="ts">
import { useClaims } from '@auth/useClaims';
import { useAdviseursGetAdviseurById } from '@services/crm-public/orval-api/adviseurs-api/adviseurs-api';
import { useRelatiesGetRelationByRelatienummer } from '@services/crm-public/orval-api/relaties-api/relaties-api';
import VIconSquare from '@shared/component/VIconSquare/VIconSquare.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, toRefs } from 'vue';

const props = defineProps<{
  relatienummer: number;
}>();

const { relatienummer } = toRefs(props);

const claims = useClaims();

const { data: relatiegegevens } = useRelatiesGetRelationByRelatienummer(relatienummer, {
  query: { select: (response) => response?.result },
});

const { data: adviseurgegevens } = useAdviseursGetAdviseurById(
  computed(() => relatiegegevens.value?.advisorId),
  {
    query: {
      select: (response) => response?.result,
      enabled: computed(
        () =>
          claims.value?.isInternal &&
          !!relatiegegevens.value &&
          relatiegegevens.value.advisorId > 0,
      ),
    },
  },
);

const tussenpersoonUrl = computed(() =>
  adviseurgegevens.value ? `/app/crm/adviseurs/${adviseurgegevens.value.id}` : '',
);
</script>

<template>
  <div
    class="flex flex-row flex-wrap items-start gap-5 px-4 py-4 text-xs text-grey-900 xl:flex-col xl:flex-nowrap xl:gap-2 xl:p-6"
  >
    <template v-if="relatiegegevens">
      <div
        class="hidden flex-row gap-x-2 gap-y-3 text-sm leading-4 font-bold overflow-wrap-anywhere xl:flex xl:pb-2"
      >
        <div>
          <VIconSquare :icon="['far', 'address-card']" />
        </div>
        <span class="flex flex-col justify-center">{{ relatiegegevens.name }}</span>
      </div>
      <div
        v-if="relatiegegevens.emailadres"
        class="flex shrink flex-row items-center gap-2 px-2 leading-4 overflow-wrap-anywhere xl:p-0"
      >
        <FontAwesomeIcon
          :icon="['far', 'envelope']"
          fixed-width
          class="shrink-0 [--fa-fw-width:16px] xl:hidden"
        />
        <a class="text-grey-600" :href="`mailto:${relatiegegevens.emailadres}`">{{
          relatiegegevens.emailadres
        }}</a>
      </div>
      <div
        v-if="relatiegegevens.telefoonnummer"
        class="flex shrink flex-row items-center gap-2 px-2 leading-4 overflow-wrap-anywhere xl:p-0"
      >
        <FontAwesomeIcon
          :icon="['far', 'phone-flip']"
          fixed-width
          class="shrink-0 [--fa-fw-width:16px] xl:hidden"
        />
        <a class="text-grey-600" :href="`tel:${relatiegegevens.telefoonnummer}`">
          {{ relatiegegevens.telefoonnummer }}</a
        >
      </div>
      <VTooltip v-if="claims?.isInternal" sticky>
        <template #content>
          <div>
            <a
              v-if="adviseurgegevens?.emailadres"
              class="text-inherit hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              :href="`mailto:${adviseurgegevens?.emailadres}`"
            >
              {{ adviseurgegevens?.emailadres }}
            </a>
            <br />
            <a
              v-if="adviseurgegevens?.telefoonnummer"
              class="text-inherit hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              :href="`tel:${adviseurgegevens.telefoonnummer}`"
            >
              {{ adviseurgegevens?.telefoonnummer }}
            </a>
          </div>
        </template>
        <div
          v-if="claims?.isInternal && adviseurgegevens"
          class="flex w-full shrink flex-row items-center gap-2 px-2 leading-4 xl:p-0"
        >
          <FontAwesomeIcon
            :icon="['far', 'user-tie']"
            fixed-width
            class="shrink-0 [--fa-fw-width:16px] xl:hidden"
          />
          <a
            v-if="claims.magAdviseurbeheerInzien"
            class="text-grey-600 xl:underline"
            :href="tussenpersoonUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ adviseurgegevens!.naam }} ({{ adviseurgegevens!.id }})
          </a>
          <span v-else class="cursor-pointer">
            {{ adviseurgegevens!.naam }} ({{ adviseurgegevens!.id }})
          </span>
        </div>
      </VTooltip>
    </template>
  </div>
</template>
